import { cn } from "@/lib/utils";
import type { TonoDelCarril } from "@/lib/generacion";

export type NumeroDeFase = 1 | 2 | 3 | 4;

export interface FaseDelCarril {
  numero: NumeroDeFase;
  nombre: string;
  /** Lo que se hace en esa fase, en una línea ("Responder las dudas"). */
  tarea: string;
  tono: TonoDelCarril;
  estado: string;
}

/** El punto de color del estado. Los tokens `*-dot` ya existen en los dos temas. */
const COLOR_DEL_PUNTO: Record<TonoDelCarril, string> = {
  reposo: "bg-ink-ghost",
  en_curso: "bg-info-dot animate-pulse",
  bloquea: "bg-danger-dot",
  aviso: "bg-warning-dot",
  ok: "bg-success-dot",
  error: "bg-danger-dot",
};

interface CarrilDeFasesProps {
  fases: FaseDelCarril[];
  activa: NumeroDeFase;
  onElegir: (fase: NumeroDeFase) => void;
}

/**
 * Las cuatro fases del informe, como un carril de tarjetas-botón.
 *
 * Es NAVEGACIÓN entre fases y no una secuencia que bloquee: el tasador puede
 * mirar cualquiera en cualquier momento (solo la EMISIÓN depende de las
 * demás). Por eso son botones siempre habilitados y no pasos de un asistente.
 *
 * Siempre cuatro columnas, como el diseño: cada tarjeta trunca su texto en vez
 * de partirse en filas. El punto de estado va junto al nombre y el estado se
 * pinta de rojo solo cuando algo bloquea.
 */
export function CarrilDeFases({ fases, activa, onElegir }: CarrilDeFasesProps) {
  return (
    <nav aria-label="Fases de la generación" className="grid grid-cols-4 gap-2">
      {fases.map((fase) => {
        const activaAhora = fase.numero === activa;
        const rojo = fase.tono === "bloquea" || fase.tono === "error";

        return (
          <button
            key={fase.numero}
            type="button"
            aria-current={activaAhora ? "step" : undefined}
            onClick={() => onElegir(fase.numero)}
            className={cn(
              "flex min-w-0 items-center gap-[0.6875rem] rounded-[0.875rem] border-[1.5px] px-3.5 py-2.5 text-left transition-colors",
              activaAhora ? "border-ink bg-card" : "border-border bg-superficie-tenue hover:border-ink-ghost",
            )}
          >
            <span
              className={cn(
                "flex size-7 shrink-0 items-center justify-center rounded-[0.5625rem] font-mono text-xs font-extrabold",
                activaAhora
                  ? "bg-ink text-background"
                  : fase.tono === "ok"
                    ? "bg-success-soft text-success-soft-foreground"
                    : "bg-divisor-fuerte text-ink-secundario",
              )}
            >
              {fase.numero}
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-[0.4375rem]">
                <span className="text-ink text-[0.8125rem] font-extrabold tracking-[-0.01em]">{fase.nombre}</span>
                <span aria-hidden className={cn("size-[0.4375rem] shrink-0 rounded-full", COLOR_DEL_PUNTO[fase.tono])} />
              </span>
              <span
                className={cn(
                  "mt-px block truncate text-[0.71875rem] font-semibold",
                  rojo ? "text-danger-texto" : "text-ink-secundario",
                )}
              >
                {fase.estado}
              </span>
              <span className="text-ink-medio mt-0.5 block truncate text-[0.65625rem] font-bold">◆ {fase.tarea}</span>
            </span>
          </button>
        );
      })}
    </nav>
  );
}
